import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { deleteProductSpecification, fetchProductSpecificationsList } from "../../shared/productChildMastersApi";
import { IconPlus } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

export function ProductSpecificationsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await fetchProductSpecificationsList();
    if (!res.ok) {
      setError(res.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(res.items);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const onEdit = (x) => {
    navigate(`/product-specifications?edit=${x.id}`);
  };

  const onDelete = async (id) => {
    if (!(await window.confirmDialog("Delete this product specification?"))) return;
    const res = await deleteProductSpecification(id);
    if (!res.ok) setError(res.error ?? "Failed to delete product specification");
    else await load();
  };

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        item,
        specificationId: item.specificationId,
        value: item.value ?? "-",
        order: item.order ?? 0,
        status: item.isActive ? "Active" : "Inactive"
      })),
    [items]
  );

  const total = rows.length;
  const columns = [
      { field: "specificationId", headerName: "Specification Id", minWidth: 140, flex: 1 },
      { field: "value", headerName: "Value", minWidth: 160, flex: 1 },
      { field: "order", headerName: "Order", width: 100 },
      { field: "status", headerName: "Status", width: 110 },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="Edit" onClick={() => onEdit(params.row.item)}>
              ✏️
            </button>
            <button className="icon-button danger" type="button" title="Delete" onClick={() => void onDelete(params.row.item.id)}>
              🗑️
            </button>
          </div>
        )
      }
  ];

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Product Specifications</h2>
          <p>{loading ? "Loading specifications..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/product-specifications" className="icon-button icon-button--toolbar" title="Add product specification" aria-label="Add product specification">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
